import { Link } from "react-router";
import { Skeleton } from "@heroui/react";
import { ArrowRightIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useTypeShelfSamples, type TypeShelfSample } from "@/hooks/useTypeShelfSamples";
import { getRecipeTypePageMetadata, recipeTypeToPath, type RecipeType } from "@/lib/types";
import { RecipeCard2 } from "../RecipeCard/RecipeCard2";
import { EmptyLibraryAlert } from "./EmptyLibraryAlert";

const TypeShelfCard = ({ sample }: { sample: TypeShelfSample }) => {
    const { recipeType, recipe, totalCount } = sample;
    const meta = getRecipeTypePageMetadata(recipeType.name as RecipeType);
    const href = `/recipes/type/${recipeTypeToPath(recipeType.name as RecipeType)}`;
    return (
        <div className="flex flex-col">
            <Link
                to={href}
                className="font-Cinzel text-xs sm:text-sm uppercase tracking-[0.18em] text-[var(--color-secondary-500)] hover:text-[var(--color-secondary-700)] transition-colors mb-3"
            >
                {meta.title}
            </Link>
            <RecipeCard2 recipe={recipe} size="sm" />
            <Link
                to={href}
                className="group mt-1 inline-flex items-center gap-1.5 font-serif italic text-sm text-[var(--accent)] hover:text-[var(--color-accent-600)] transition-colors self-start"
            >
                <span>
                    See all {totalCount} {totalCount === 1 ? "recipe" : "recipes"}
                </span>
                <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
        </div>
    );
};

const SkeletonShelf = () => (
    <div className="flex flex-col">
        <Skeleton className="h-4 w-32 rounded mb-3" />
        <Skeleton className="h-44 w-full rounded-lg mb-3" />
        <Skeleton className="h-4 w-20 rounded mb-2" />
        <Skeleton className="h-6 w-3/4 rounded" />
    </div>
);

export const HomeShelves = () => {
    const { data, isLoading, isError } = useTypeShelfSamples(3);
    const isEmpty = !isLoading && !isError && (!data || data.length === 0);

    return (
        <section className="bg-[var(--bg)] px-6 sm:px-12 py-16 sm:py-20">
            <div className="m-auto max-w-5xl">
                <header className="mb-10 sm:mb-12">
                    <h2 className="font-Cinzel text-3xl sm:text-4xl uppercase tracking-[0.2em] text-[var(--color-secondary-500)] mb-3">
                        From the Grimoire
                    </h2>
                    <p className="font-serif italic text-lg sm:text-xl text-[var(--fg-muted)]">Conjured at random.</p>
                </header>

                {isError && (
                    <div role="alert" className="flex flex-col items-center justify-center text-[var(--fg-muted)]">
                        <ExclamationTriangleIcon className="h-10 w-10 text-danger-500 mb-3" />
                        <span className="font-serif italic">Could not load recipes.</span>
                    </div>
                )}

                {isLoading && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <SkeletonShelf key={i} />
                        ))}
                    </div>
                )}

                {!isLoading && !isError && data && data.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
                        {data.map((sample) => (
                            <TypeShelfCard key={sample.recipeType.id} sample={sample} />
                        ))}
                    </div>
                )}

                {isEmpty && <EmptyLibraryAlert />}
            </div>
        </section>
    );
};
